import axios from 'axios';
import * as authenticationApiService from '@common/api/authentication';
import { clientCleanup } from '@common/api/httpClient';
import * as restApiService from '@common/api/rest';
import { type LogViewingRights } from '@common/hooks/useLogViewingRights';
import { getLocale } from '@common/i18n/supportedLocales';
import { type AppDispatch } from '@common/store';
import {
  addDeviceDetailsAction,
  addNotificationContactAction,
  deleteNotificationContactAction,
  removeUserAction,
  setContractTypesAction,
  setDeviceTypesAction,
  setLanguagesAction,
  setLogTypesAction,
  setTimeZonesAction,
  setUserAction,
  setUserStateAction,
  updateNotificationContactAction,
} from '@common/store/reducers';
import { clearFeatureFlags } from '@common/store/reducers/featureFlags';
import {
  type ColorsStoreState,
  DefaultColorPalette,
  maxPaletteSize,
  setColors,
} from '@common/store/reducers/graphs';
import {
  type CurrentUserUpdateRequest,
  type Retailer,
  type SubscriberCreate,
  type SubscriberUpdate,
} from '@common/types/api';
import { type InstallationId } from '@common/types/brandedTypes';
import { compareFullName } from '@common/types/models/AdditionalUser';
import { UnknownUser } from '@common/types/models/User';
import { UserState } from '@common/types/store';
import { isHiddenFromAdvancedUser } from '@common/utils/device/isModuleOfType';
import sessionStorageManager from '@common/utils/storage/sessionStorageManager';
import {
  SESSION_CHECK_USER_TIMEZONE_KEY,
  USER_PREFERENCE_COLOR_MAPPING_PREFIX,
  USER_PREFERENCE_COLOR_PALETTE,
} from '@common/utils/storage/storageKeys';

/** Ends the session on this device without calling the backend; protected pages then go to login. */
export function endSession() {
  return (dispatch: AppDispatch) => {
    clientCleanup();
    dispatch(removeUserAction());
  };
}

export function getCurrentUser() {
  return async (dispatch: AppDispatch) => {
    try {
      const user = await authenticationApiService.fetchCurrentUser();
      dispatch(setUserAction({
        ...user,
        locale: await getLocale(user.language),
      }));
    } catch {
      dispatch(endSession());
    }
  };
}

export function updateCurrentUserAndDispatch(request: CurrentUserUpdateRequest) {
  return async (dispatch: AppDispatch) => {
    const user = await authenticationApiService.updateCurrentUser(request);
    dispatch(setUserAction({
      ...user,
      locale: await getLocale(user.language),
    }));
  };
}

export function getTimeZones() {
  return async (dispatch: AppDispatch) => {
    dispatch(setTimeZonesAction(await restApiService.fetchTimeZones()));
  };
}

export function login(userName: string, password: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(clearFeatureFlags());
    await authenticationApiService.login(userName, password);
    try {
      sessionStorageManager.saveItem<boolean>(SESSION_CHECK_USER_TIMEZONE_KEY, true);
      const user = await authenticationApiService.fetchCurrentUser();
      dispatch(setUserAction({
        ...user,
        locale: await getLocale(user.language),
      }));
      dispatch(setUserStateAction(UserState.LoggedIn));
    } catch (err) {
      dispatch(endSession());
      throw err;
    }
  };
}

export function logout() {
  return async (dispatch: AppDispatch) => {
    const success = await authenticationApiService.logout();
    if (success) {
      dispatch(removeUserAction());
    }
  };
}

export function getContractTypes() {
  return async (dispatch: AppDispatch) => {
    const types = await restApiService.fetchContractTypes();
    dispatch(setContractTypesAction(types));
  };
}

export function getDeviceTypes() {
  return async (dispatch: AppDispatch) => {
    const types = (await restApiService.fetchAllDeviceTypes())
      ?.filter((type) => !isHiddenFromAdvancedUser(type.name));
    if (types) {
      dispatch(setDeviceTypesAction(types));
    }
  };
}

export function getLogTypes(
  logViewingRights: LogViewingRights,
  signal: AbortSignal,
) {
  return async (dispatch: AppDispatch) => {
    const types = await restApiService.fetchLogTypes(logViewingRights, signal);
    if (types) {
      dispatch(setLogTypesAction(types));
    }
  };
}

export function getDeviceByInstallationId(installationId: InstallationId) {
  return async (dispatch: AppDispatch) => {
    const device = await restApiService.fetchDeviceByInstallationId(installationId);
    if (device) {
      dispatch(addDeviceDetailsAction(device));
    }
  };
}

export function getDeviceOwner(
  ownerId: string,
  installationId: InstallationId,
) {
  return async (dispatch: AppDispatch) => {
    // An owner that cannot be loaded is shown as unknown rather than failing the device page
    const owner = await authenticationApiService.fetchUserDetailsById(ownerId)
      .catch(() => UnknownUser);
    dispatch(addDeviceDetailsAction({
      owner,
      id: installationId,
    }));
  };
}

export function getDeviceUsers(
  installationId: InstallationId,
) {
  return async (dispatch: AppDispatch) => {
    const additionalUsers = (await restApiService.fetchAdditionalUsers(installationId))
      .filter((user) => !user.isDeviceOwner)
      .sort(compareFullName);
    dispatch(addDeviceDetailsAction({
      additionalUsers,
      id: installationId,
    }));
    return additionalUsers;
  };
}

async function getPartners(
  installationId: InstallationId,
  dispatch: AppDispatch,
  signal: AbortSignal,
) {
  try {
    const [partners, allPartners] = await Promise.all([
      restApiService.fetchRetailerAccess(installationId, signal),
      authenticationApiService.fetchPartners(signal),
    ]);
    if (partners) {
      dispatch(addDeviceDetailsAction({
        partners: partners.map((item): Retailer => {
          const found = allPartners.find(({ id }) => item.retailerId === id);
          return {
            ...item,
            customerNumber: found?.customerNumber,
          };
        }),
        id: installationId,
      }));
    }
  } catch (err) {
    if (!axios.isCancel(err)) {
      throw err;
    }
  }
}

export function getDeviceBackupRestores(
  installationId: InstallationId,
  signal: AbortSignal,
) {
  return async (dispatch: AppDispatch) => {
    const restores = await restApiService.fetchBackupRestores(installationId, signal);
    dispatch(addDeviceDetailsAction({
      backupRestores: restores.toSorted((a, b) => b.startUtc.localeCompare(a.startUtc)),
      id: installationId,
    }));
  };
}

export function getDeviceIntegrations(
  installationId: InstallationId,
  signal: AbortSignal,
) {
  return async (dispatch: AppDispatch) => {
    const integrations = await restApiService.fetchInstallationIntegrations(installationId, signal);
    dispatch(addDeviceDetailsAction({
      integrations,
      id: installationId,
    }));
  };
}

interface DeviceGeneralDataOptions {
  installationId: InstallationId;
  fetchPartners: boolean;
  fetchIntegrations: boolean;
  isEndUser: boolean;
  signal: AbortSignal;
}

export function getDeviceGeneralData({
  installationId,
  fetchPartners,
  fetchIntegrations,
  isEndUser,
  signal,
}: DeviceGeneralDataOptions) {
  return (dispatch: AppDispatch) => {
    async function getDeviceComment() {
      const comment = await restApiService.fetchDeviceComment(installationId, signal);
      dispatch(addDeviceDetailsAction({
        comment,
        id: installationId,
      }));
    }

    async function getContract() {
      const contract = await restApiService.fetchDeviceContract(installationId, signal);
      if (contract) {
        dispatch(addDeviceDetailsAction({
          contract,
          id: installationId,
        }));
      }
    }

    async function getNotifications() {
      const notificationContacts = await restApiService.fetchNotificationContacts(
        installationId,
        signal,
      );

      dispatch(addDeviceDetailsAction({
        notificationContacts,
        id: installationId,
      }));
    }

    if (!isEndUser) {
      getDeviceComment();
      getContract();
      dispatch(getDeviceBackupRestores(installationId, signal));
    }
    if (fetchPartners) {
      getPartners(installationId, dispatch, signal);
    }
    if (fetchIntegrations) {
      dispatch(getDeviceIntegrations(installationId, signal));
    }
    getNotifications();
  };
}

export function updatePartners(
  installationId: InstallationId,
  signal: AbortSignal,
) {
  return (dispatch: AppDispatch) => getPartners(installationId, dispatch, signal);
}

export function addNotificationContact(
  installationId: InstallationId,
  subscriber: SubscriberCreate,
) {
  return async (dispatch: AppDispatch) => {
    const { data } = await restApiService.createNotificationContact(subscriber, installationId);
    dispatch(addNotificationContactAction({ installationId, subscriber: data }));
  };
}

export function deleteNotificationContact(
  installationId: InstallationId,
  notificationContactId: number,
) {
  return async (dispatch: AppDispatch) => {
    await restApiService.deleteNotificationContact(notificationContactId);
    dispatch(deleteNotificationContactAction({ installationId, notificationContactId }));
  };
}

export function updateNotificationContact(
  installationId: InstallationId,
  subscriber: SubscriberUpdate,
) {
  return async (dispatch: AppDispatch) => {
    await restApiService.updateNotificationContact(subscriber.id, installationId, subscriber);
    dispatch(updateNotificationContactAction({ installationId, subscriber }));
  };
}

export function getLanguages() {
  return async (dispatch: AppDispatch) => {
    const languages = await restApiService.fetchLanguages();
    dispatch(setLanguagesAction(languages));
  };
}

export function fetchColors(controllerUnit?: string) {
  return async (dispatch: AppDispatch) => {
    let palette = await restApiService.fetchUserPreferenceSwallowingAllErrors<
      ColorsStoreState['colorPalette']
    >(
      USER_PREFERENCE_COLOR_PALETTE,
    );
    if (!palette || palette.length !== maxPaletteSize) {
      palette = [...DefaultColorPalette];
    }

    let mapping: ColorsStoreState['colorMapping'] = {};
    if (controllerUnit) {
      const preferenceKey = USER_PREFERENCE_COLOR_MAPPING_PREFIX
        + (controllerUnit ?? 'UNKNOWN').replace(/\s/g, '');
      mapping = await restApiService.fetchUserPreferenceSwallowingAllErrors<
        ColorsStoreState['colorMapping']
      >(preferenceKey)
        ?? {};
    }

    dispatch(setColors({ colorPalette: palette, colorMapping: mapping }));
  };
}
